import { Body, Divider, Flex, Heading } from '@hover/blueprint';
import convert from 'color-convert';
import React, { useContext, useMemo } from 'react';

import { ScrollMenu } from 'src/modules/designStudio/components/DesignMenu/ScrollMenu';
import { hexToVector } from 'src/modules/designStudio/helpers/colorUtils';
import type { ProductsRouter } from 'src/modules/designStudio/helpers/productSelection/ProductsRouter';
import {
  ColorGroupMatchingHex,
  ColorGroupNames,
} from 'src/modules/designStudio/helpers/productSelection/VariationColorGroups';
import {
  AllProductTypeLabels,
  VariationColorGroup,
} from 'src/modules/designStudio/helpers/productSelection/types';
import { productCatalogContext } from 'src/modules/designStudio/providers';

const createGlobalColorGrouping = (
  productsRouter: ProductsRouter | undefined,
): Record<VariationColorGroup, string[]> => {
  const colorGroups: Record<VariationColorGroup, Set<string>> = {
    BEIGE: new Set<string>(),
    BLACK: new Set<string>(),
    BLUE: new Set<string>(),
    BROWN: new Set<string>(),
    GRAY: new Set<string>(),
    GREEN: new Set<string>(),
    PURPLE: new Set<string>(),
    RED: new Set<string>(),
    WHITE: new Set<string>(),
    YELLOW: new Set<string>(),
  };

  if (productsRouter) {
    AllProductTypeLabels.forEach(type => {
      const products = productsRouter.getProducts(type);
      products.forEach(product =>
        product.variations.forEach(v => {
          if (v.color?.group) {
            colorGroups[v.color.group].add(v.color.hex);
          }
        }),
      );
    });
  }

  return {
    BEIGE: [...colorGroups[VariationColorGroup.BEIGE].values()],
    BLACK: [...colorGroups[VariationColorGroup.BLACK].values()],
    BLUE: [...colorGroups[VariationColorGroup.BLUE].values()],
    BROWN: [...colorGroups[VariationColorGroup.BROWN].values()],
    GRAY: [...colorGroups[VariationColorGroup.GRAY].values()],
    GREEN: [...colorGroups[VariationColorGroup.GREEN].values()],
    PURPLE: [...colorGroups[VariationColorGroup.PURPLE].values()],
    RED: [...colorGroups[VariationColorGroup.RED].values()],
    WHITE: [...colorGroups[VariationColorGroup.WHITE].values()],
    YELLOW: [...colorGroups[VariationColorGroup.YELLOW].values()],
  };
};

export const ColorGroupDebugger = () => {
  const { pcDataStore } = useContext(productCatalogContext);
  const colorGroupings = useMemo(
    () => createGlobalColorGrouping(pcDataStore),
    [pcDataStore],
  );

  const title = 'Color Grouping Menu';
  const text = `
  This menu shows all of the unique colors pulled from all the products
          exposed by product catalog, grouped into their respective color
          groups. This menu aggregates the color of all wall, roof, and trim
          products.`;

  return (
    <ScrollMenu.Menu>
      <ScrollMenu.Header>
        <Heading>{title}</Heading>
      </ScrollMenu.Header>
      <ScrollMenu.Body>
        <Body>{text}</Body>
        <Flex direction="column" gap={400}>
          {Object.entries(colorGroupings).map(([colorEnum, colors]) => {
            const name = ColorGroupNames[colorEnum as VariationColorGroup];
            const groupHex =
              ColorGroupMatchingHex[colorEnum as VariationColorGroup];
            const groupHSL = convert.hex.hsl(groupHex);
            return (
              <>
                <Heading>{name}</Heading>
                <Flex
                  alignItems="center"
                  bg={groupHex}
                  border="solid"
                  borderRadius="8px"
                  height="45px"
                  justifyContent="center"
                  width="100%"
                >
                  <Body>{JSON.stringify(groupHSL, undefined, 0)}</Body>
                </Flex>
                <Flex
                  alignContent="space-between"
                  direction="row"
                  flexWrap="wrap"
                  gap={100}
                  justifyContent="space-between"
                >
                  {colors.map(hex => {
                    const v = hexToVector(hex);
                    const otherHSL = convert.rgb.hsl(v.x, v.y, v.z);
                    return (
                      <Flex
                        alignItems="center"
                        bg={hex}
                        border={name === 'White' ? 'solid black' : ''}
                        borderRadius="8px"
                        direction="column"
                        fontSize="4px"
                        justifyContent="center"
                        minWidth="90px"
                        p="8px"
                      >
                        <Body m={0} p={0}>
                          {hex}
                        </Body>
                        <Body m={0} p={0}>
                          {JSON.stringify(otherHSL, undefined, 0)}
                        </Body>
                      </Flex>
                    );
                  })}
                </Flex>
                <Divider />
              </>
            );
          })}
        </Flex>
      </ScrollMenu.Body>
    </ScrollMenu.Menu>
  );
};
